import { CSSResultGroup, TemplateResult, nothing } from 'lit';
import { html, unsafeStatic } from 'lit/static-html.js';
import { customElement, property, state } from 'lit/decorators.js';
import { SlotController } from '../../controllers/slot-controller';
import { screenSizeMap } from '../../controllers/screen-size-controller';
import { classMap } from 'lit/directives/class-map.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { SicAccordionContainer } from '../sic-accordion-container/sic-accordion-container';
import defaultButtonVars from '../sic-cta/css-variables/vars-default.css.lit';
import buttonStyles from '../sic-cta/sic-cta.lightdom.css.lit';
import '../sic-icon/sic-icon';
import '../sic-cta/sic-cta';
import componentStyles from './sic-accordion.css.lit';

export type AllowedHeadingLevels = 'h2' | 'h3' | 'h4';
export type AllowedThemeValues = 'normal' | 'minimal';

/**
 * The Sic Accordion component
 * @element sic-accordion
 * @extends OutlineElement
 *
 * @slot heading: The title text for the panel component.
 * @slot default slot: The main panel content, visible when the panel is open.
 * @note Removed the `alt` property, it can be used to make the accordion wrap
 *       other accordions better. See example from [Saul Ewing](https://github.com/phase2/saul-ewing-design/tree/next/src/components/se-design/se-accordion).
 *
 * @attr {boolean} active - whether the panel is active/open
 * @attr {string} id
 * @attr {string} heading-level {'h2','h3','h4'} (default - 'h3') - heading tag that wraps the heading slot
 * @attr {boolean} close-button - whether the panel shows a close button at the bottom.
 * @attr {string} theme ['normal','minimal'] - controls style of accordion button and panel content.
 */

@customElement('sic-accordion')
export class SicAccordion extends OutlineElement {
  static styles: CSSResultGroup = [
    componentStyles,
    defaultButtonVars,
    buttonStyles,
  ];

  slots = new SlotController(
    this, // This, the host element.
    false // To shift or not to shift LightDom nodes to ShadowDOM.
  );
  /**
   * Wether the panel is active/open.
   * Controlled by parent accordion component.
   */
  @property({ type: Boolean, reflect: true, attribute: true })
  active = false;

  /**
   * Used to provided a unique ID
   * for accordion component and accessibility purposes.
   */
  @property({ type: String, reflect: true })
  id: string = self.crypto.randomUUID
    ? self.crypto.randomUUID()
    : self.crypto.getRandomValues(new Uint32Array(3)).join('-');

  /**
   * Heading level to use for for label.
   * defaults to 'h3'
   */
  @property({ type: String, attribute: 'heading-level', reflect: true })
  headingLevel: AllowedHeadingLevels = 'h3';

  /**
   * Optionally render a "close" button at the end of the accordion.
   *
   */
  @property({ type: Boolean, reflect: true, attribute: 'close-button' })
  closeButton = false;

  /**
   * Theme controls style of accordion button and panel content.
   */
  @property({ type: String, attribute: 'theme', reflect: true })
  theme: AllowedThemeValues = 'normal';

  @state() private isMobile: boolean;

  firstUpdated(): void {
    this.requestUpdate();
    //@todo testing the size make sure to change
    const maxWidthScreen = screenSizeMap.get('md') as number;
    this.mobileMode(maxWidthScreen);
  }

  mobileMode(width: number) {
    const maxMobileWidth = width - 1;
    const mediaQuery = `(max-width: ${maxMobileWidth}px)`;
    const mediaQueryList = window.matchMedia(mediaQuery);
    // Set initial value to isMobile
    this.isMobile = mediaQueryList.matches;

    // update isMobile value only when screen width is crossing maxMobileWidth (1024)
    mediaQueryList.addEventListener('change', event => {
      this.isMobile = event.matches;
      this.requestUpdate();
    });
  }

  render(): TemplateResult {
    const isMobile = this.isMobile ? 'mobile' : null;
    const isActive = this.active ? 'active' : null;

    const buttonClasses = {
      'accordion-button': true,
      'mobile': this.isMobile,
      'active': this.active,
      'inactive': !this.active,
      'minimal': this.theme == 'minimal',
    };

    const iconClasses = {
      'accordion-icon': true,
      'mobile': this.isMobile,
      'active': this.active,
    };

    return html` <div class="accordion-panel ${isActive}">
        <${unsafeStatic(this.headingLevel as string)} class="accordion-heading">
          <button
            class="${classMap(buttonClasses)}"
            id="${this.id}-button"
            aria-expanded=${this.active}
            aria-controls="${this.id}-info"
            @click=${this.setActive}
          >
            <span class="accordion-label ${isMobile}">
              <slot @click="${this.passFocus}" name="label-content"> </slot>
            </span>
            <span class="${classMap(iconClasses)}"> ${this.setIcon()} </span>
          </button>
        </${unsafeStatic(this.headingLevel as string)}>
        <div
          class="accordion-content ${isMobile}"
          aria-labelledby="${this.id}-button"
          id="${this.id}-info"
          ?hidden="${!this.active}"
        >
          <slot></slot>
          ${
            this.closeButton
              ? html` <sic-cta>
                  <button
                    class="btt-button"
                    id="${this.id}-button-close"
                    aria-expanded=${this.active}
                    aria-controls="${this.id}-info"
                    @click=${this.handleSectionClose}
                  >
                    Close This Section
                  </button>
                </sic-cta>`
              : nothing
          }
        </div>
      </div>
      <hr />`;
  }

  passFocus() {
    const parentButton = this.shadowRoot?.querySelector(
      '.accordion-button'
    ) as HTMLButtonElement;
    if (parentButton) {
      parentButton.focus();
    }
  }

  setActive() {
    const parentWrapper = this.parentElement as SicAccordionContainer;
    const singleActive = parentWrapper.singleActive;

    if (singleActive) {
      const activePanels = [...parentWrapper.panels].filter(
        panel => panel.active == true
      );

      if (activePanels.length < 1) {
        this.active = true;
      }

      if (activePanels.length > 0) {
        activePanels.map(panel => {
          if (panel.id === this.id) {
            this.active = false;
          } else {
            panel.active = false;
            this.active = true;
          }
        });
      }
    } else {
      this.active = !this.active;
    }
  }

  setIcon() {
    if (this.active) {
      return html`<sic-icon
        name="chevron-up"
        size="20px"
        svg-fill="currentColor"
      ></sic-icon> `;
    } else {
      return html`<sic-icon
        name="chevron-down"
        size="20px"
        svg-fill="currentColor"
      ></sic-icon>`;
    }
  }

  handleSectionClose() {
    this.setActive();
    this.scrollIntoView(true);
  }
}
